import { forwardRef, useEffect, useRef, useState } from 'react'

import styled from 'styled-components'

import { ProjectFloor } from '../../../../../api/types'
import iconZoomInBlue from '../../../../../assets/images/icon_zoom_in_blue.svg'
import iconZoomOutBlue from '../../../../../assets/images/icon_zoom_out_blue.svg'
import { IconButton } from '../../../../common/IconButton'
import { LoadingIndicator } from '../../../../common/LoadingIndicator'
import { MapViewer } from '../../../../common/MapViewer'

import { useScanHealthFloorQuery } from './useScanHealthQueries'

/** Keeps markers ~this many screen pixels wide at any zoom (see Safety Gallery). */
const SCAN_HEALTH_POINT_SCREEN_SIZE = 16

const LOAD_ROOT_MARGIN = '200px 0px'

interface ScanHealthFloorSectionProps {
  floor: ProjectFloor
  projectId: string | undefined
  registerSection: (floorCode: string, element: HTMLElement | null) => void
  scrollRoot: HTMLElement | null
}

export const ScanHealthFloorSection = forwardRef<
  HTMLElement,
  ScanHealthFloorSectionProps
>(function ScanHealthFloorSection(
  { floor, projectId, registerSection, scrollRoot },
  forwardedRef,
) {
  const sectionRef = useRef<HTMLElement | null>(null)
  const [shouldLoad, setShouldLoad] = useState(false)

  const {
    data: floorData,
    isLoading,
    isError,
  } = useScanHealthFloorQuery(projectId, floor, shouldLoad)

  const setSectionRef = (node: HTMLElement | null) => {
    sectionRef.current = node
    if (typeof forwardedRef === 'function') {
      forwardedRef(node)
    } else if (forwardedRef) {
      forwardedRef.current = node
    }
  }

  useEffect(() => {
    const element = sectionRef.current
    if (!element) return

    registerSection(floor.floor_code, element)
    return () => registerSection(floor.floor_code, null)
  }, [floor.floor_code, registerSection])

  useEffect(() => {
    const element = sectionRef.current
    if (!element || shouldLoad) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true)
          observer.disconnect()
        }
      },
      { root: scrollRoot, rootMargin: LOAD_ROOT_MARGIN },
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [shouldLoad, floor.floor_code, scrollRoot])

  const mapRef = useRef<MapViewer | null>(null)
  const [loadedImg, setLoadedImg] = useState('')

  const imageUrl = floorData?.imageUrl
  const imageLoaded = !!imageUrl && loadedImg === imageUrl

  return (
    <Section ref={setSectionRef} data-floor-code={floor.floor_code}>
      <FloorHeader>{floor.name}</FloorHeader>

      {isError && (
        <InlineError>Unable to load scan data for this floor.</InlineError>
      )}

      {!isError && !shouldLoad && (
        <MapPlaceholder data-scan-health-map>
          <LoadingIndicator />
        </MapPlaceholder>
      )}

      {!isError && shouldLoad && isLoading && (
        <MapPlaceholder data-scan-health-map>
          <LoadingIndicator />
        </MapPlaceholder>
      )}

      {!isError && shouldLoad && !isLoading && !imageUrl && (
        <EmptyMessage>No floor plan available for this floor.</EmptyMessage>
      )}

      {!isError && shouldLoad && !isLoading && imageUrl && (
        <MapContainer data-scan-health-map>
          {!imageLoaded && <LoadingIndicator />}
          <MapViewer
            ref={mapRef}
            image={imageUrl}
            setLoadedImg={setLoadedImg}
            selectedDateRange={null}
            imageLoaded={imageLoaded}
            points={floorData?.points ?? []}
            showPoints={true}
            scaleInvariantPointSize={SCAN_HEALTH_POINT_SCREEN_SIZE}
          />
          <IconButton
            icon={iconZoomInBlue}
            size={40}
            primary={false}
            onClick={() => mapRef.current?.zoomIn()}
            style={{
              position: 'absolute',
              right: '16px',
              top: 'calc(50% - 22px)',
              zIndex: 2,
            }}
          />
          <IconButton
            icon={iconZoomOutBlue}
            size={40}
            primary={false}
            onClick={() => mapRef.current?.zoomOut()}
            style={{
              position: 'absolute',
              right: '16px',
              top: 'calc(50% + 22px)',
              zIndex: 2,
            }}
          />
        </MapContainer>
      )}
    </Section>
  )
})

const Section = styled.section`
  margin-bottom: 48px;
`

const FloorHeader = styled.h2`
  margin: 0 0 12px;
  color: #333;
  font-size: 18px;
  font-weight: 600;
`

const MapContainer = styled.div`
  position: relative;
  width: 100%;
  height: min(70vh, 720px);
  min-height: 400px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
  background: #f8f8f8;
`

const MapPlaceholder = styled.div`
  position: relative;
  width: 100%;
  height: min(70vh, 720px);
  min-height: 400px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
  background: #f8f8f8;
`

const EmptyMessage = styled.p`
  color: #686868;
  font-size: 14px;
  margin: 0;
`

const InlineError = styled.p`
  color: #c62828;
  font-size: 14px;
  margin: 0 0 12px;
`
